import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { ArrowRight, Sparkles, Wallet, ShieldCheck, Users, CheckCircle2, Car, Mail, Trophy, TrendingUp, Download } from "lucide-react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/f523a880-996a-4f26-bc1c-5695f9a74996/images/1f1c3552cc07b16d05ef5466db21750523daf8511410f1c34beb968d48195d02.png";
const LIFESTYLE = "https://images.unsplash.com/photo-1760042770723-5b3324aad901?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxpdGFsaWFuJTIwY291bnRyeXNpZGUlMjBkcml2aW5nfGVufDB8fHx8MTc3NzcxMTM4N3ww&ixlib=rb-4.1.0&q=85";
const DETAIL = "https://images.unsplash.com/photo-1774810264294-71b4948dfbeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjYXIlMjBsaWZlc3R5bGV8ZW58MHx8fHwxNzc3NzExMzg3fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setBoard(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F8F9]" data-testid="landing-page">
      <Header variant="marketing" />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 lg:pt-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 fade-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-600 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-slate-200">
              <Sparkles className="w-3.5 h-3.5 text-[#F39C12]" /> Programma fedeltà 2026
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#0F7B85]">
              Presenta un amico<br/>
              <span className="text-[#F39C12] font-light italic">risparmia fino a 100€</span><br/>
              sulla tua RCA.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              Ogni amico che sottoscrive una polizza con Besafe Broker, vale <strong>10€ di abbuono</strong>
              {" "}sulla tua prossima scadenza. Cumulabile fino a 100€. Trasparente, etico, rispettoso della privacy.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/registrati"
                data-testid="hero-cta-register"
                className="bg-[#0F7B85] text-white hover:bg-[#0A5C64] rounded-lg px-7 py-3.5 font-medium inline-flex items-center gap-2 transition-colors"
              >
                Inizia ora <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login"
                data-testid="hero-cta-login"
                className="border border-[#0F7B85] text-[#0F7B85] hover:bg-[#0F7B85] hover:text-white rounded-lg px-7 py-3.5 font-medium transition-colors"
              >
                Sono già cliente
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-slate-500">
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#0F7B85]"/> GDPR-compliant</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#0F7B85]"/> Doppio consenso</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#0F7B85]"/> Trasparente</div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up-d2">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl aspect-[4/5]">
              <img src={LIFESTYLE} alt="Italia" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F7B85]/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-xs uppercase tracking-[0.2em] opacity-80">Esempio</div>
                <div className="font-display text-2xl mt-1">3 amici × 10€ = <span className="text-[#FCD9A0]">30€</span></div>
                <div className="text-xs opacity-70 mt-2">Cumulabile fino a 100€</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS - Bento */}
      <section id="come-funziona" className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-12 max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Come funziona</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0F7B85]">
            Tre passi. Niente sorprese.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
          <div className="md:col-span-4 lg:col-span-5 bg-[#0F7B85] text-white rounded-2xl p-8 lg:p-10 lg:row-span-2 flex flex-col justify-between min-h-[320px]">
            <div>
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-6">
                <Car className="w-6 h-6" />
              </div>
              <div className="text-xs uppercase tracking-[0.2em] opacity-70">Step 01</div>
              <h3 className="font-display text-2xl mt-2 leading-tight">Segnala il tuo amico in 30 secondi</h3>
              <p className="mt-3 text-white/80 text-sm leading-relaxed">
                Ti bastano nome, email e cellulare dell'amico. Alla targa ci penserà lui
                quando compilerà il preventivo. Doppio consenso GDPR garantito.
              </p>
            </div>
            <img src={DETAIL} alt="" className="rounded-xl mt-6 aspect-video object-cover" />
          </div>

          <div className="md:col-span-4 lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Step 02</div>
            <div className="flex items-center gap-3 mt-2">
              <Mail className="w-6 h-6 text-[#F39C12]" />
              <h3 className="font-display text-2xl text-[#0F7B85]">Il tuo amico riceve un'email</h3>
            </div>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Riceve un invito personalizzato con il tuo nome. Confermando, autorizza un nostro consulente a
              contattarlo per un preventivo dedicato.
            </p>
          </div>

          <div className="md:col-span-4 lg:col-span-7 bg-[#ECEEF0] border border-slate-200 rounded-2xl p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Step 03</div>
            <div className="flex items-center gap-3 mt-2">
              <Wallet className="w-6 h-6 text-[#0F7B85]" />
              <h3 className="font-display text-2xl text-[#0F7B85]">+10€ sul tuo wallet</h3>
            </div>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Quando l'amico sottoscrive la polizza, accreditiamo automaticamente <strong>10€</strong> sul tuo
              wallet. Verranno scontati alla prossima scadenza RCA. Massimo cumulabile: 100€.
            </p>
          </div>
        </div>
      </section>

      {/* LEADERBOARD - Social proof */}
      <section id="leaderboard" className="bg-[#F7F8F9] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20" data-testid="leaderboard-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#F39C12]" /> Top Referrer del mese
              </div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0F7B85]">
                {board && board.top && board.top.length > 0 ? (
                  <>C&apos;è chi ci sta già <span className="italic font-light">guadagnando</span>.</>
                ) : (
                  <>Diventa il primo a <span className="italic font-light">guadagnare</span>.</>
                )}
              </h2>
              <p className="mt-4 text-slate-600 max-w-xl leading-relaxed">
                {board && board.top && board.top.length > 0 ? (
                  <>Una piccola community di clienti Besafe che presentano amici e accumulano abbuoni.
                  I dati sono aggiornati in tempo reale, i nomi sono anonimizzati per privacy.</>
                ) : (
                  <>Il programma è appena partito: chi inizia ora ha un&apos;intera leaderboard
                  da conquistare e 12 mesi davanti per arrivare a 100€ di sconto.</>
                )}
              </p>
            </div>
            {board && board.total_referrals >= 10 && (
              <div className="lg:col-span-5 grid grid-cols-3 gap-4">
                <StatTile label="Segnalazioni" value={board.total_referrals} testid="board-stat-referrals" />
                <StatTile label="Sottoscritte" value={board.subscribed} accent testid="board-stat-subscribed" />
                <StatTile label="Erogati" value={`${board.credits_distributed}€`} testid="board-stat-credits" />
              </div>
            )}
          </div>

          {board && board.top.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="leaderboard-list">
              {board.top.slice(0, 3).map((row, i) => (
                <PodiumCard key={i} rank={i + 1} row={row} />
              ))}
              {board.top.length > 3 && (
                <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  {board.top.slice(3).map((row, i) => (
                    <div key={i + 3} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 text-xs font-mono flex items-center justify-center font-semibold">{i + 4}</div>
                        <div className="text-sm font-medium text-slate-800">{row.name}</div>
                        {row.tier && row.tier.id !== "starter" && (
                          <span
                            className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: `${row.tier.color}1A`, color: row.tier.color }}
                          >
                            {row.tier.name}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-mono text-[#0F7B85]">{row.referrals} amici · <span className="text-[#F39C12]">{row.credits}€</span></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
              <TrendingUp className="w-10 h-10 text-[#0F7B85] mx-auto opacity-50" />
              <div className="mt-4 font-display text-xl text-[#0F7B85]">Sii il primo questo mese.</div>
              <p className="mt-2 text-sm text-slate-600">Apri il programma e presenta il tuo primo amico per comparire qui.</p>
              <Link
                to="/registrati"
                className="inline-block mt-5 bg-[#F39C12] text-white hover:bg-[#E08E10] rounded-lg px-5 py-2.5 text-sm font-medium transition-colors"
                data-testid="leaderboard-empty-cta"
              >
                Inizia ora
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Regolamento */}
      <section id="regolamento" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Regolamento ufficiale</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-[#0F7B85]">
                "Besafe ti premia"
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Regolamento completo dell'iniziativa promossa da BeSafe Broker srl. Trasparente,
                conforme al GDPR. Puoi scaricare il documento ufficiale in PDF.
              </p>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/regolamento.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                download="regolamento-besafe-ti-premia.pdf"
                data-testid="regolamento-download-btn"
                className="mt-6 inline-flex items-center gap-2 bg-[#0F7B85] text-white hover:bg-[#0A5C64] rounded-lg px-5 py-3 font-medium transition-colors"
              >
                <Download className="w-4 h-4" /> Scarica il regolamento (PDF)
              </a>
              <div className="mt-6 text-xs text-slate-500">
                BeSafe Broker srl · Via Emerico Amari, 8 – 90139 Palermo (PA)<br/>
                Titolare del trattamento: privacy@besafebroker.it
              </div>
            </div>
            <div className="lg:col-span-7 space-y-5">
              <RegolamentoArticle
                num="01" title="Promotore e Finalità"
                text="L'iniziativa è promossa da BeSafe Broker srl (Palermo) per premiare la fedeltà dei clienti e incentivare l'acquisizione di nuove polizze Rc Auto, Moto, Ciclomotori e Furgoni."
              />
              <RegolamentoArticle
                num="02" title="Requisiti di Partecipazione"
                text="Partecipano i Clienti Presentatori (già titolari di una polizza attiva con BeSafe) che invitano un Cliente Presentato (nuovo cliente o cliente attivo che assicura un veicolo mai gestito da BeSafe negli ultimi 24 mesi)."
              />
              <RegolamentoArticle
                num="03" title="Meccanismo e Canali di Diffusione"
                text="Partecipazione tramite Link Personale univoco generato da BeSafe, condivisibile via email, SMS o WhatsApp. L'acquisto della polizza del Presentato deve avvenire tramite il link per garantire il tracciamento."
              />
              <RegolamentoArticle
                num="04" title="Valore e Applicazione dell'Abbuono"
                text="Abbuono commerciale di €10,00 per ogni polizza emessa. Cumulabilità massima: €100,00 per anno assicurativo. Detratto dal premio al primo rinnovo utile. Non cedibile, non convertibile, decade in caso di mancato rinnovo."
              />
              <RegolamentoArticle
                num="05" title="Protezione dei Dati"
                text="Trattamento conforme al GDPR (Reg. UE 2016/679). Titolare: BeSafe Broker srl. Dati trattati esclusivamente per la gestione dell'iniziativa promozionale."
              />
              <RegolamentoArticle
                num="06" title="Informazioni Legali"
                text="BeSafe Broker s.r.l. è un intermediario assicurativo iscritto alla Sezione B del RUI con n. B000725320, soggetto alla vigilanza dell'IVASS. Consulta gli estremi dell'iscrizione su www.servizi.ivass.it."
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-5xl mx-auto px-6 py-20">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">FAQ</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-[#0F7B85]">
            Domande frequenti
          </h2>
        </div>
        <div className="mt-12 space-y-4">
          {[
            ["Quando ricevo l'abbuono?", "Riceverai i 10€ sul tuo wallet non appena l'amico segnalato sottoscrive effettivamente una polizza con noi. Ti avvisiamo via email."],
            ["L'abbuono scade?", "No, ma viene utilizzato alla prima scadenza utile della tua polizza RCA dopo l'accredito."],
            ["Cosa succede se ho già 100€?", "Le segnalazioni successive non generano nuovi abbuoni: il cap massimo è 100€. Potrai partecipare di nuovo dopo che gli abbuoni saranno stati utilizzati."],
            ["I dati del mio amico sono al sicuro?", "Sì. Trattiamo i dati nel pieno rispetto del GDPR. Richiediamo doppio consenso e l'amico riceve sempre un'email di doppia conferma prima di essere contattato."],
            ["Posso segnalare la stessa persona più volte?", "No: una stessa email (o targa, se l'hai indicata) non può essere segnalata due volte dallo stesso cliente."],
          ].map(([q, a], i) => (
            <details
              key={i}
              data-testid={`faq-item-${i}`}
              className="group bg-white border border-slate-200 rounded-xl px-6 py-4 open:shadow-sm transition"
            >
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <span className="font-medium text-[#0F7B85]">{q}</span>
                <span className="text-[#F39C12] text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-slate-600 leading-relaxed text-sm">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative bg-[#0F7B85] rounded-3xl p-10 lg:p-16 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#F39C12]/20 blur-3xl"/>
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold">Pronto a iniziare?</div>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
                Trasforma le tue raccomandazioni<br/> in <span className="italic font-light text-[#FCD9A0]">abbuoni reali</span>.
              </h2>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/registrati"
                data-testid="cta-bottom-register"
                className="bg-[#F39C12] text-white hover:bg-[#E08E10] rounded-lg px-8 py-4 font-medium inline-flex items-center gap-2 transition-colors"
              >
                Crea il tuo account <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <div className="font-display text-[#0F7B85] text-base mb-1.5">BeSafe Broker s.r.l.</div>
              <div className="text-slate-500 leading-relaxed">
                Via Emerico Amari, 8<br/>
                90139 Palermo (PA)<br/>
                <a href="mailto:promozioni@besafebroker.it" className="hover:text-[#0F7B85]">promozioni@besafebroker.it</a>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">Link utili</div>
              <div className="flex flex-col gap-1.5 text-slate-600">
                <a href="#regolamento" className="hover:text-[#0F7B85]">Regolamento dell'iniziativa</a>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/regolamento.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#0F7B85]"
                  data-testid="footer-pdf-link"
                >Scarica regolamento (PDF)</a>
                <Link to="/privacy" className="hover:text-[#0F7B85]" data-testid="landing-footer-privacy">Informativa Privacy</Link>
                <a href="https://besafebroker.it/cookie-policy-ue/" target="_blank" rel="noopener noreferrer" className="hover:text-[#0F7B85]" data-testid="landing-footer-cookie">Cookie Policy ↗</a>
                <a href="https://besafebroker.it/contatti/" target="_blank" rel="noopener noreferrer" className="hover:text-[#0F7B85]" data-testid="landing-footer-contacts">Contatti ↗</a>
                <a href="https://besafebroker.it" target="_blank" rel="noopener noreferrer" className="hover:text-[#0F7B85] font-semibold" data-testid="landing-footer-main-site">Sito ufficiale ↗</a>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">Vigilanza</div>
              <div className="text-slate-600 leading-relaxed text-xs">
                Intermediario assicurativo iscritto alla <strong>Sezione B del RUI</strong> con n. <strong>B000725320</strong>,
                soggetto alla vigilanza dell'<strong>IVASS</strong>.
                <br/>
                Consulta gli estremi dell'iscrizione su{" "}
                <a href="https://servizi.ivass.it" target="_blank" rel="noopener noreferrer" className="text-[#0F7B85] hover:underline">www.servizi.ivass.it</a>.
              </div>
            </div>
          </div>
          <div className="border-t border-slate-200 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
            <div>© 2026 BeSafe Broker s.r.l. · Tutti i diritti riservati</div>
            <div>P.IVA / C.F. <span className="font-mono">07093890825</span></div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function StatTile({ label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl p-5 border text-center ${accent ? "bg-[#0F7B85] text-white border-[#0F7B85]" : "bg-white border-slate-200"}`}
    >
      <div className={`font-display text-3xl ${accent ? "" : "text-[#0F7B85]"}`}>{value}</div>
      <div className={`text-[10px] uppercase tracking-[0.18em] mt-1 font-semibold ${accent ? "text-white/70" : "text-slate-500"}`}>{label}</div>
    </div>
  );
}

function PodiumCard({ rank, row }) {
  const medals = {
    1: { bg: "bg-gradient-to-br from-[#F39C12] to-[#E08E10]", emoji: "🥇", border: "border-[#F39C12]" },
    2: { bg: "bg-gradient-to-br from-slate-400 to-slate-500", emoji: "🥈", border: "border-slate-300" },
    3: { bg: "bg-gradient-to-br from-amber-700 to-amber-800", emoji: "🥉", border: "border-amber-600" },
  };
  const m = medals[rank] || medals[3];
  return (
    <div className={`bg-white border-2 ${m.border} rounded-2xl p-6 relative overflow-hidden`} data-testid={`podium-${rank}`}>
      <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full ${m.bg} opacity-20 blur-2xl`} />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className={`w-10 h-10 rounded-xl ${m.bg} text-white flex items-center justify-center font-display font-bold text-lg`}>
            {rank}°
          </div>
          <span className="text-2xl">{m.emoji}</span>
        </div>
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <div className="font-display text-xl text-[#0F7B85] truncate">{row.name}</div>
          {row.tier && row.tier.id !== "starter" && (
            <span
              data-testid={`podium-tier-${row.tier.id}`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${row.tier.color}1A`, color: row.tier.color, border: `1px solid ${row.tier.color}40` }}
              title={`Livello ${row.tier.name}`}
            >
              {row.tier.name}
            </span>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Amici</div>
            <div className="font-mono text-lg text-slate-800">{row.referrals}</div>
          </div>
          <div className="bg-[#F39C12]/10 rounded-lg p-2.5 border border-[#F39C12]/20">
            <div className="text-[10px] uppercase tracking-wider text-[#F39C12] font-semibold">Abbuono</div>
            <div className="font-mono text-lg text-[#F39C12]">{row.credits}€</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RegolamentoArticle({ num, title, text }) {
  return (
    <div className="flex gap-4 bg-[#F7F8F9] border border-slate-200 rounded-xl p-5" data-testid={`regolamento-art-${num}`}>
      <div className="w-11 h-11 rounded-lg bg-[#0F7B85] text-white flex items-center justify-center font-display font-semibold shrink-0">
        {num}
      </div>
      <div>
        <div className="font-display text-base text-[#0F7B85]">{title}</div>
        <div className="text-sm text-slate-600 mt-1 leading-relaxed">{text}</div>
      </div>
    </div>
  );
}

